import { createApp } from "@backstage/app-defaults";
import { AppRouter, FlatRoutes } from "@backstage/core-app-api";
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from "@backstage/core-components";
import { microsoftAuthApiRef } from "@backstage/core-plugin-api";
import { apiDocsPlugin, ApiExplorerPage } from "@backstage/plugin-api-docs";
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from "@backstage/plugin-catalog";
import { catalogEntityCreatePermission } from "@backstage/plugin-catalog-common/alpha";
import { CatalogGraphPage } from "@backstage/plugin-catalog-graph";
import {
  CatalogImportPage,
  catalogImportPlugin,
} from "@backstage/plugin-catalog-import";
import { HomepageCompositionRoot } from "@backstage/plugin-home";
import { orgPlugin } from "@backstage/plugin-org";
import { RequirePermission } from "@backstage/plugin-permission-react";
import { ScaffolderPage, scaffolderPlugin } from "@backstage/plugin-scaffolder";
import { ScaffolderFieldExtensions } from "@backstage/plugin-scaffolder-react";
import { SearchPage } from "@backstage/plugin-search";
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from "@backstage/plugin-techdocs";
import { ReportIssue } from "@backstage/plugin-techdocs-module-addons-contrib";
import { TechDocsAddons } from "@backstage/plugin-techdocs-react";
import { UserSettingsPage } from "@backstage/plugin-user-settings";
import { UnifiedThemeProvider } from "@backstage/theme";
import NightsStay from "@material-ui/icons/NightsStay";
import WbSunny from "@material-ui/icons/WbSunny";
import { Route } from "react-router-dom";

import { apis } from "./apis";
import { entityPage } from "./components/catalog/EntityPage";
import { HomePage } from "./components/home/HomePage";
import { Root } from "./components/Root";
import { searchPage } from "./components/search/SearchPage";
import { ValidateJiraKeyFieldExtension } from "./scaffolder/ValidateJiraKey";
import { ValidateKebabCaseFieldExtension } from "./scaffolder/ValidateKebabCase";
import { kvDarkTheme } from "./theme/kvDark";
import { kvLightTheme } from "./theme/kvLight";

import "./styles.css";



const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: (props) => {
      // const configApi = useApi(configApiRef);
      // const isDev = configApi.getString("auth.environment") === "development";

      const providers = [
        {
          id: "entra-auth-provider",
          title: "Microsoft Entra",
          message: "Sign in using Microsoft Entra",
          apiRef: microsoftAuthApiRef,
        },
      ];

      return (
        <SignInPage
          {...props}
          auto
          providers={providers}
        />
      );
    },
  },
  themes: [
    {
      id: "kv-dark",
      title: "Kinetic Vision Dark Mode",
      variant: "dark",
      icon: <NightsStay />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={kvDarkTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: "kv-light",
      title: "Kinetic Vision Light Mode",
      variant: "light",
      icon: <WbSunny />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={kvLightTheme} >
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <ValidateKebabCaseFieldExtension />
        <ValidateJiraKeyFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
